import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Content, Title } from '@radix-ui/react-dialog';

import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { LIVE_SYSTEM_MESSAGES_URL } from '@/pages/ManagementArea/SystemMessages/systemMessages';
import SystemMessageCard from './SystemMessageCard';
import { clearLegacySeenRecord, legacySeenMessageIds } from './legacySeenRecord';
import styles from './SystemMessagesPopup.module.scss';

/**
 * The announcements an admin defined in the management area, popped up once per message per account
 * (SPR-1074). The new site's replacement for the old one's startup message, which showed a single
 * toast from the navbar.
 *
 * Several live messages are paged through one at a time rather than stacked: each announcement gets
 * the whole card, its own icon and its own buttons, which is what makes it readable - a column of
 * them would turn every one into a fragment.
 *
 * ⚠️ Per ACCOUNT, not per browser (HAT-1340). Closing the popup marks its messages read on the
 * server, the same record the updates bell has kept since HAT-1099, so a coordinator who signs in
 * from a second computer is not interrupted by announcements they have already dealt with. The
 * reporter had in fact marked both of the live ones read on the bell and was still being stopped
 * here every morning, because this dialog kept its own record in localStorage and never looked at
 * theirs.
 *
 * The two records could not be shared before: the bell's badge would have been zeroed by a popup
 * that is dismissed within a second of signing in, long before anyone opened the panel. ShowAsPopup
 * is what settled that - the popup now only carries messages whose author asked them to interrupt,
 * so a message reaching both surfaces is one the user really has been shown.
 */
const SystemMessagesPopup = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const { data: liveMessages, refetch } = useGetAuthApi<SystemMessageDtoIn[]>(
    currentUser ? LIVE_SYSTEM_MESSAGES_URL : '',
  );

  const [isDismissed, setIsDismissed] = useState(false);
  const [index, setIndex] = useState(0);

  /*
   * What this session has already marked, so the dialog does not come back between the POST and the
   * refetch that reflects it - or at all, if the POST fails and the server still reports it unread.
   * A ref rather than state: it must not itself cause a render, and it is only ever read while
   * deciding what to show.
   */
  const markedInThisSession = useRef<Set<number>>(new Set());

  /*
   * Which messages to show is decided once per fetch: recomputing it while the dialog is open would
   * let it empty out under the user as soon as they are marked read.
   *
   * ⚠️ Only the ones whose author asked them to interrupt. The rest are still live and still
   * reachable - they wait in the updates bell instead (HAT-1099).
   */
  const popupMessages = useMemo(
    () => (liveMessages ?? []).filter((message) => message.showAsPopup),
    [liveMessages],
  );

  /*
   * The old per-browser record, read here rather than only in the migration effect below: effects
   * run after the render they belong to, so waiting for one would flash the dialog on screen before
   * hiding it again. Reading is free of side effects, and the record is not cleared until the
   * server has the same information.
   */
  const legacySeen = useMemo(() => new Set(legacySeenMessageIds(popupMessages)), [popupMessages]);

  const messagesToShow = useMemo(
    () => popupMessages.filter((message) => (
      !message.isRead && !markedInThisSession.current.has(message.id) && !legacySeen.has(message.id)
    )),
    [popupMessages, legacySeen],
  );

  const markRead = async (messages: SystemMessageDtoIn[]) => {
    const unread = messages.filter((message) => !message.isRead);

    if (unread.length === 0) return;

    unread.forEach((message) => markedInThisSession.current.add(message.id));

    try {
      const headers = { Authorization: `Bearer ${currentUser?.token}` };

      // One call per message rather than read-all: "all" is every live announcement, including the
      // ones this dialog was never allowed to show.
      await Promise.all(unread.map((message) => postAuth<void>(`system-messages/${message.id}/read`, headers, {})));

      // The bell reads this same cache entry, so its badge drops with the popup instead of waiting
      // for a reload. Its own refetch, not invalidateQueries - see UpdatesBell for why.
      await refetch();
    } catch {
      // The user saw the message and said so; the worst case is being asked again on the next load,
      // which is where this feature already was. Nothing useful can be shown for it here.
    }
  };

  /*
   * The one-time move of the old per-browser record onto the account (HAT-1340). Without it every
   * user who had dismissed today's announcements in a browser - and almost all of them have, since
   * the bell that writes server rows has only been released since 07/09 - would be shown them once
   * more on the deploy.
   *
   * Once per mount: the record cannot grow again, so a second pass has nothing left to find.
   */
  const migratedRef = useRef(false);

  useEffect(() => {
    if (!currentUser || migratedRef.current || !liveMessages) return;

    migratedRef.current = true;

    const seen = popupMessages.filter((message) => legacySeen.has(message.id));

    if (seen.length === 0) {
      clearLegacySeenRecord();
      return;
    }

    // Cleared only once the server has accepted them - a failed request must not lose the
    // dismissal in both places at once.
    void markRead(seen).then(clearLegacySeenRecord);
    // markRead is rebuilt every render and would restart this; the fetched list is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, liveMessages]);

  // A new set of messages arriving (a login, a refetch) is a new popup, not the dismissed one.
  useEffect(() => {
    setIsDismissed(false);
    setIndex(0);
  }, [liveMessages]);

  if (!currentUser || isDismissed || messagesToShow.length === 0) return null;

  const total = messagesToShow.length;
  const activeIndex = Math.min(index, total - 1);
  const isLast = index >= total - 1;

  const close = () => {
    // Only the set this dialog offered - closing on the first of three still dismisses all three,
    // as it always has, and the bell is where they remain reachable. Marking anything beyond it
    // would record a reading that did not happen, and on the bell, which shares the record, that
    // message would quietly leave the unread count.
    void markRead(messagesToShow);
    setIsDismissed(true);
  };

  // On the last message the primary button closes; before that it walks the user along, so there is
  // only ever one obvious thing to press.
  const handlePrimary = () => (isLast ? close() : setIndex((value) => value + 1));

  return (
    <Root open onOpenChange={(isOpen) => !isOpen && close()}>
      <Portal>
        <Overlay className={styles.overlay} />
        <Content className={styles.content} aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}>
          <button type="button" className={styles.closeButton} onClick={close} aria-label={t('close')}>
            <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <path d="M5.5 18.5 18.5 5.5M18.5 18.5 5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>

          {/* Every message is laid out, all of them in the same grid cell, and only the current one
              is made visible. The deck is therefore always as tall as the longest message, so paging
              between them does not resize the dialog under the reader's hands. Hidden ones stay
              rendered - `display: none` would take them out of the grid and bring the resizing back -
              and `visibility: hidden` keeps them out of the tab order and off the screen reader. */}
          <div className={styles.body}>
            <div className={styles.deck}>
              {messagesToShow.map((message, cardIndex) => {
                const isActive = cardIndex === activeIndex;

                return (
                  <div key={message.id} className={`${styles.deckItem} ${isActive ? styles.deckItemActive : ''}`} aria-hidden={isActive ? undefined : true}>
                    {/* Only the visible card may be the dialog's Title: Radix gives every Title the
                        same id, so a second one would duplicate it in the document. */}
                    <SystemMessageCard message={message} TitleTag={isActive ? Title : 'h2'} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.footer}>
            {total > 1 ? (
              <div className={styles.pager}>
                <span className={`${styles.pagerCount} body-sm-regular`}>{t('systemMessages.popupCounter', { current: index + 1, total })}</span>
                <div className={styles.dots}>
                  {messagesToShow.map((message, dotIndex) => (
                    <button
                      key={message.id}
                      type="button"
                      className={`${styles.dot} ${dotIndex === index ? styles.dotActive : ''}`}
                      onClick={() => setIndex(dotIndex)}
                      aria-label={t('systemMessages.popupGoTo', { number: dotIndex + 1 })}
                      aria-current={dotIndex === index ? 'true' : undefined}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <span />
            )}

            <div className={styles.footerActions}>
              {total > 1 && (
                <button type="button" className={`${styles.stepButton} body-sm-semibold`} onClick={() => setIndex((value) => value - 1)} disabled={index === 0}>
                  <StepArrow direction="back" />
                  {t('systemMessages.popupPrevious')}
                </button>
              )}
              <button type="button" className={`${styles.primaryButton} body-sm-semibold`} onClick={handlePrimary}>
                {isLast ? t('systemMessages.gotIt') : t('systemMessages.popupNext')}
              </button>
            </div>
          </div>
        </Content>
      </Portal>
    </Root>
  );
};

/** The arrow itself always points left; the stylesheet flips it for right-to-left. */
const StepArrow = ({ direction }: { direction: 'back' | 'forward' }) => (
  <svg
    className={styles.stepIcon}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
    style={direction === 'forward' ? { transform: 'rotate(180deg)' } : undefined}
  >
    <path d="M19 12H6m0 0 5.5-5.5M6 12l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SystemMessagesPopup;
